<header class="relative px-4 pb-4 pt-2">
  <h1 class="text-2xl font-semibold text-greyscale-900">
    {{ showDeleted ? "Deleted companies" : "Companies" }}
  </h1>
  <p class="text-sm text-greyscale-600">
    @if (showDeleted) {
      Reactivate companies that were previously deleted
    } @else {
      Manage travel agencies and tour operators using Greentripper
    }
  </p>
  <div class="absolute right-4 top-2 flex items-center gap-2">
    <p-button
      [label]="showDeleted ? 'Back to active' : 'Deleted companies'"
      [icon]="showDeleted ? 'pi pi-arrow-left' : 'pi pi-trash'"
      [outlined]="true"
      severity="secondary"
      (onClick)="toggleDeleted()"
    />
    @if (!showDeleted) {
      <app-download-company-list />
      <p-button label="Add Company" icon="pi pi-plus" routerLink="create" />
    }
  </div>
</header>

<main class="p-4">
  <p-table
    #dt
    [value]="filteredCompanies"
    [paginator]="true"
    [rows]="20"
    [globalFilterFields]="['name', 'pack', 'adminEmail']"
    styleClass="p-datatable-sm"
  >
    <ng-template #caption>
      <div class="flex flex-wrap items-end gap-4">
        <div class="min-w-[140px]">
          <span class="mb-1 block text-sm font-medium">Plan</span>
          <p-select
            [options]="packOptions()"
            [(ngModel)]="packFilter"
            optionLabel="label"
            optionValue="value"
            placeholder="All plans"
            [style]="{ width: '100%' }"
          />
        </div>
        <div class="min-w-[140px]">
          <span class="mb-1 block text-sm font-medium">Status</span>
          <p-select
            [options]="statusOptions"
            [(ngModel)]="statusFilter"
            optionLabel="label"
            optionValue="value"
            placeholder="All statuses"
            [style]="{ width: '100%' }"
          />
        </div>
        <div>
          <p-button
            icon="pi pi-filter-slash"
            (onClick)="resetFilters()"
            [text]="true"
            [rounded]="true"
            pTooltip="Reset filters"
          />
        </div>
        <div class="ml-auto min-w-[200px]">
          <span class="mb-1 block text-sm font-medium">Search</span>
          <p-iconfield iconPosition="left">
            <p-inputicon>
              <i class="pi pi-search"></i>
            </p-inputicon>
            <input
              pInputText
              type="text"
              [(ngModel)]="globalFilter"
              (ngModelChange)="dt.filterGlobal(globalFilter, 'contains')"
              placeholder="Search companies..."
              class="w-full"
            />
          </p-iconfield>
        </div>
      </div>
    </ng-template>

    <ng-template #header>
      <tr>
        <th>Company</th>
        <th>Plan</th>
        <th>Status</th>
        <th>Users</th>
        <th>Trips</th>
        <th>Contact</th>
        <th>Last Activity</th>
        @if (showDeleted) {
          <th>Actions</th>
        }
      </tr>
    </ng-template>

    <ng-template #emptymessage>
      <tr>
        <td [attr.colspan]="showDeleted ? 8 : 7" class="py-12 text-center">
          <div class="flex flex-col items-center gap-4">
            <i class="pi pi-building text-6xl text-greyscale-300"></i>
            <div class="flex flex-col gap-1">
              <h3 class="text-lg font-semibold text-greyscale-700">
                @if (showDeleted) {
                  No deleted companies
                } @else {
                  No companies found
                }
              </h3>
              <p class="text-sm text-greyscale-500">
                @if (globalFilter || packFilter || statusFilter) {
                  Try adjusting your search or filter criteria
                } @else if (showDeleted) {
                  Deleted companies will appear here
                } @else {
                  Get started by adding your first company
                }
              </p>
            </div>
            @if (!showDeleted && !globalFilter && !packFilter && !statusFilter) {
              <p-button
                label="Add Company"
                icon="pi pi-plus"
                routerLink="create"
                styleClass="mt-2"
              />
            }
          </div>
        </td>
      </tr>
    </ng-template>

    <ng-template #body let-company>
      <tr
        class="h-14 hover:bg-gray-50"
        [class.cursor-pointer]="!showDeleted"
        (click)="editCompany(company.id)"
      >
        <!-- Company -->
        <td>
          <div class="flex flex-col">
            <span class="font-medium">{{ company.name ?? "--" }}</span>
          </div>
        </td>

        <!-- Plan -->
        <td>
          <span class="text-sm font-medium">{{ company.pack.name }}</span>
        </td>

        <!-- Status (compact avec compteur) -->
        <td>
          <div class="flex flex-col gap-1">
            <p-tag
              class="w-fit border text-xs"
              [ngClass]="getTagClasses(company)"
            >
              <i
                [class]="getCompactStatus(company).icon"
                class="mr-1 text-xs"
              ></i>
              {{ getCompactStatus(company).label }}
            </p-tag>
            @if (getCompactStatus(company).progress) {
              <div class="flex items-center gap-1">
                <i class="pi pi-chart-line text-xs text-greyscale-500"></i>
                <span class="font-mono text-xs text-greyscale-600">
                  {{ getCompactStatus(company).progress }} trips
                </span>
              </div>
            }
          </div>
        </td>

        <!-- Users -->
        <td>
          <span class="text-sm">{{ company._count?.users ?? "--" }}</span>
        </td>

        <!-- Trips -->
        <td>
          <span class="text-sm">{{ company._count?.trips ?? "--" }}</span>
        </td>

        <!-- Contact (email + sales info) -->
        <td>
          <div class="flex flex-col gap-1">
            @if (company.adminEmail) {
              <div class="flex items-center gap-1">
                <i class="pi pi-user text-xs text-greyscale-500"></i>
                <span class="max-w-32 truncate text-xs text-greyscale-700">{{
                  company.adminEmail
                }}</span>
              </div>
            }
            @if (company._count?.salesLeads > 0) {
              <div class="flex items-center gap-1">
                <i class="pi pi-envelope text-xs text-primary-500"></i>
                <span class="text-xs text-primary-600"
                  >{{ company._count.salesLeads }} lead(s)</span
                >
              </div>
            }
            @if (company.contactedSalesAt) {
              <div class="flex items-center gap-1">
                <i class="pi pi-calendar text-xs text-greyscale-500"></i>
                <span class="text-xs text-greyscale-500">{{
                  formatDate(company.contactedSalesAt).split(" ")[0]
                }}</span>
              </div>
            }
          </div>
        </td>

        <!-- Last Activity -->
        <td>
          <span class="text-sm text-greyscale-700">{{
            formatDate(company.lastConnection)
          }}</span>
        </td>

        <!-- Actions (deleted mode only) -->
        @if (showDeleted) {
          <td>
            <p-button
              label="Reactivate"
              icon="pi pi-refresh"
              size="small"
              [outlined]="true"
              (onClick)="confirmReactivateCompany(company.id); $event.stopPropagation()"
            />
          </td>
        }
      </tr>
    </ng-template>
  </p-table>
</main>

<p-confirmdialog rejectButtonStyleClass="p-button-outlined" />
<p-toast />
